<template>
  <div class="board">
    <div v-for="rowIndex in maxRows" :key="rowIndex" class="board-row">
      <div v-for="col in solution.length" :key="col" class="board-cell">
        <div class="board-cell-inner" :class="cellClass(rowIndex - 1, col - 1)">
          <span>
            {{ letterAt(rowIndex - 1, col - 1) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "BoardGrid",
  props: {
    solution: { type: String, required: true },
    guesses: { type: Array, required: true },
    maxRows: { type: Number, default: 8 },
    currentGuess: { type: String, default: "" },
  },
  methods: {
    // y a-t-il déjà une lettre "correct" à cette colonne sur une des lignes passées ?
    hasCorrectAt(col) {
      for (const g of this.guesses) {
        if (g.statuses && g.statuses[col] === "correct") {
          return true;
        }
      }
      return false;
    },

    letterAt(row, col) {
      // lignes déjà validées
      if (row < this.guesses.length) {
        const word = this.guesses[row].word;
        return word[col] || "";
      }

      // ligne en cours
      if (row === this.guesses.length) {
        const typed = this.currentGuess[col];

        // si le joueur a tapé une lettre, on l'affiche
        if (typed) {
          return typed;
        }

        // sinon, si une lettre a déjà été trouvée ici, on préremplit visuellement
        if (this.hasCorrectAt(col)) {
          return this.solution[col];
        }

        // sinon, point de remplissage
        return ".";
      }

      return "";
    },

    cellClass(row, col) {
      // lignes déjà validées
      if (row < this.guesses.length) {
        const guess = this.guesses[row];
        const status = guess.statuses[col];
        if (status === "correct") return "correct reveal";
        if (status === "present") return "present reveal";
        if (status === "absent") return "absent reveal";
        return "empty reveal";
      }

      // ligne en cours
      if (row === this.guesses.length) {
        const typed = this.currentGuess[col];

        // lettre tapée par le joueur
        if (typed) {
          const alreadyCorrectHere = this.hasCorrectAt(col);
          const isSameAsSolution = typed === this.solution[col];

          // Anim uniquement si on écrit une lettre "non déjà trouvée"
          if (!alreadyCorrectHere || !isSameAsSolution) {
            return "typing typing-pop";
          }
          return "typing";
        }

        return "empty";
      }

      return "empty";
    },
  },
};
</script>

<style scoped>
.board {
  display: inline-block;
  background: var(--bg);
  padding: 0;
}

/* lignes */
.board-row {
  display: flex;
}

/* case de base : la GRILLE (fond + bordure), reste fixe */
.board-cell {
  width: 48px;
  height: 48px;
  box-sizing: border-box;
  border: 2px solid var(--tile-border); /* lignes blanches */
  margin: 0;
  background: var(--bg); /* bleu de fond */
  display: flex;
  align-items: center;
  justify-content: center;
}

/* contenu animé / coloré dans une inner cell */
.board-cell-inner {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 33px;
  text-transform: uppercase;
  color: #ffffff;
}

.board-cell-inner span {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ligne vide */
.board-cell-inner.empty {
  background: transparent;
}

/* ligne en cours : lettres tapées + points */
.board-cell-inner.typing {
  background: var(--tile-typing);
}

/* animation uniquement quand on tape une lettre “nouvelle” */
.board-cell-inner.typing-pop {
  animation: cell-pop 120ms ease-out;
  transform-origin: center;
}

@keyframes cell-pop {
  0% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

/* --- Rouge plein pour les lettres "correct" --- */
.board-cell-inner.correct {
  background: var(--tile-correct);
  color: #ffffff;
}

/* --- Jaune en grand rond qui remplit la case --- */
.board-cell-inner.present {
  background: transparent;
}

.board-cell-inner.present span {
  background: var(--tile-present); /* jaune */
  color: #ffffff;
  border-radius: 50%;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* lettres absentes : juste lettre blanche sur fond bleu */
.board-cell-inner.absent {
  background: transparent;
  color: #ffffff;
}

/* animation de validation (ligne validée) */
.board-cell-inner.reveal {
  animation: reveal-pop 180ms ease-out;
}

@keyframes reveal-pop {
  0% {
    transform: scale(0);
  }
  70% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

/* responsive */
@media (max-width: 600px) {
  .board-cell {
    width: 38px;
    height: 38px;
  }

  .board-cell-inner {
    font-size: 20px;
  }

  .board-cell-inner.present span {
    width: 100%;
    height: 100%;
  }
}
</style>
